/**
 * REPORT-PAGE-CONTENT.WIDGET.TSX
 * Edit content for a page
 */
import ImagePicker from "@/components/image-picker"
import ListDropdown from "@/components/list-dropdown"
import { store } from "@/index"
import { useEffect, useState } from "react"
import { WithTranslation, withTranslation } from "react-i18next"
import { connect } from "react-redux"
import { pageEdit, pageFormatEditorState } from "@/redux/page.actions"
import {
  PAGE_CONTENT_SCREENSHOT_CONFIGS,
  Page,
  PageContent,
  PageContentScreenshot,
  PageContentScreenshotParam,
  PageSide,
  PageState
} from "@/redux/page.types"
import { v4 as uuid } from "uuid"
import { Editor } from "react-draft-wysiwyg"
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css"
import { EditorState, convertToRaw } from "draft-js"
import { Session } from "@/redux/_session.types"
import { useClickOutside } from "react-click-outside-hook"
import env from "@/env"
import { reportEdit, reportGetTagsFromTexts, reportUpdateTagsData } from "@/redux/report.actions"
import { ReportState } from "@/redux/report.types"
import { keys, merge, snakeCase } from "lodash"
import { SurveyState } from "@/redux/survey.types"
import ReportScreenshotGlobalResultsWidget from "./report-screenshot-global-results.widget"
import ReportScreenshotAxesWidget from "./report-screenshot-axes.widget"
import ReportScreenshotPodiumWidget from "./report-screenshot-podium.widget"
import ReportScreenshotQuestionsWidget from "./report-screenshot-questions.widget"
import ListItem from "@/components/list-item"
import { Topic, TopicState } from "@/redux/topic.types"
import Checkbox from "@/components/checkbox"
import Space from "@/components/space"
import Link from "@/components/link"
import ReportScreenshotHeatmapWidget from "./report-screenshot-heatmap.widget"
import { ACCOUNT_RESULTS_TYPES } from "@/redux/account.types"
import { DashboardFilters, FilterState } from "@/redux/filter.types"
import { filterReplaceDashboard } from "@/redux/filter.actions"
import { faFont, faImage } from "@fortawesome/free-solid-svg-icons"
import EditbarButton from "@/components/editbar-button"
import { FadeIn } from "@/utils/animations.utils"
import { STATUS_SAVING } from "@/redux/_status.types"
import { topicGroupForReport } from "@/redux/topic.actions"
import ReportScreenshotMessagesWidget from "./report-screenshot-messages.widget"
import { AxisState } from "@/redux/axis.types"
import ReportScreenshotNpsWidget from "./report-screenshot-nps.widget"
import ReportScreenshotRepartitionWidget from "./report-screenshot-repartition.widget"
import ReportScreenshotParticipationWidget from "./report-screenshot-participation.widget"
import ReportScreenshotHistoryWidget from "./report-screenshot-history.widget"
import ReportScreenshotRpsTeamsWidget from "./report-screenshot-rps-teams.widget"
import ReportScreenshotRpsQuestionsWidget from "./report-screenshot-rps-questions.widget"
import DashboardRpsOverviewWidget from "./dashboard-rps-overview.widget"
import DashboardRpsUsersWidget from "./dashboard-rps-users.widget"
import DashboardRpsGollacOverwiewWidget from "./dashboard-rps-gollac-overwiew.widget"
import { DashboardRpsGollacTeamsWidget } from "./dashboard-rps-gollac-teams.widget"
import DashboardRpsGollacQuestionsWidget from "./dashboard-rps-gollac-questions.widget"
import { DashboardRpsGollacWidget } from "./dashboard-rps-gollac.widget"

interface StateProps extends WithTranslation {
  _session: Session
  axis: AxisState
  filter: FilterState
  page: PageState
  report: ReportState
  survey: SurveyState
  topic: TopicState
}

interface OwnProps {
  pageContent: PageContent
  currentPage: Page
  isRight?: boolean
  isSmall?: boolean
}

type Props = StateProps & OwnProps

function ReportPageContentWidget(props: Props) {
  const { t } = props

  //Init editor State
  //> editorState is the raw text for edition (with the variable as "survey_name")
  //> editorStateFormatted include the translation for the variables
  const [editorState, setEditorState] = useState(EditorState.createEmpty())
  const [editorStateFormatted, setEditorStateFormatted] = useState(EditorState.createEmpty())
  const [pageContent, setPageContent] = useState(props.pageContent)

  //Init is edited
  const [isEdited, setIsEdited] = useState(false)

  //The user have click to edit
  const [isClicked, setIsClicked] = useState(false)

  //Detect click outiside the component => will set isEdited as false
  const [ref, hasClickedOutside] = useClickOutside()

  //The crop modal of the image picker is open
  //> it is rendered in a portal, so any click inside it is seen as a click outside
  const [isCropping, setIsCropping] = useState(false)

  //Click oustide
  //> ignored while the crop modal is open
  useEffect(() => {
    if (!isCropping) setIsEdited(false)
  }, [hasClickedOutside, isCropping])

  //Detect when isEdited is switching from true to false
  //> edit the store
  useEffect(() => {
    async function editEnd() {
      if (!isEdited && isClicked) {
        //Edit text blocks
        const rawBlocks = convertToRaw(editorState.getCurrentContent()).blocks
        setEditorStateFormatted(pageFormatEditorState(rawBlocks, true))
        edit("textBlocks", rawBlocks)

        //Update tags data
        const contentTags = reportGetTagsFromTexts(rawBlocks.map((x) => x.text))
        const contentTagsData = await reportUpdateTagsData(contentTags)
        if (keys(contentTagsData).length > 0) {
          store.dispatch(reportEdit("tagsData", merge(props.report.active.tagsData, contentTagsData)))
        }
      }
    }

    editEnd()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdited, isClicked])

  //Detect stasus changed betweed isEdited (for disable navigation with key)
  useEffect(() => {
    store.dispatch(reportEdit("keyDownAllowed", !isEdited))
  }, [isEdited])

  //Update state when page is changed or tags data is updated
  useEffect(() => {
    setPageContent(props.pageContent)
    setEditorStateFormatted(pageFormatEditorState(props.pageContent.textBlocks, true))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.page.active.id])

  //Init image ID for upload image
  const [imageId] = useState(uuid())

  //Add or remove topic for the list of topic to hide
  function displayTopic(topic: Topic) {
    if (topic.aid) {
      const hideItems = !pageContent.screenshot.isTopicHidden(topic)
        ? pageContent.screenshot.hideItems.concat([topic.aid])
        : pageContent.screenshot.hideItems.filter((x) => x !== topic.aid)
      editScreenshot("hideItems", hideItems)
    }
  }

  //Change the content (nested param)
  function edit(key: string, value: any) {
    const contentKey: PageSide = props.isRight ? "contentRight" : "contentLeft"
    const newPageContent = new PageContent({
      ...props.page.active[contentKey],
      [key]: value
    })

    editPage(contentKey, newPageContent)
    setPageContent(newPageContent)
  }

  //Edit page
  function editPage(key: string, value: any) {
    store.dispatch(pageEdit(key, value))
  }

  //Edit screenshot value
  function editScreenshot(key: string, value: any) {
    const newScreenshot = new PageContentScreenshot({
      ...pageContent.screenshot,
      [key]: value
    })

    edit("screenshot", newScreenshot)

    // Update dashboard filters for native filters
    if (key === "gender" || key === "birthDate" || key === "companyWelcomeDate") {
      const newDashboardFilters = new DashboardFilters({
        ...props.filter.dashboard,
        [key]: value
      })
      store.dispatch(filterReplaceDashboard(newDashboardFilters))
    }
  }

  //Return screenshot
  //If slide display the widget of the dashboard
  //If preview (isSmall) display image
  function getScreenshot() {
    return (
      <div
        id={
          props.isSmall
            ? undefined
            : "image-exportable-report-screenshot-" + (props.isRight ? "right" : "left") + "-" + props.currentPage.id
        }
        style={{ maxHeight: 334 }}>
        {props._session.interfaceType === "SUPERVISOR"
          ? t("page_type_screenshot_" + pageContent.screenshot.type)
          : getScreenshotWidget()}
      </div>
    )
  }

  //Return associated screenshot
  //Do not reload data for each card if only preview
  function getScreenshotWidget() {
    //Init current topics array
    const currentTopics =
      props.page.status === STATUS_SAVING
        ? props.currentPage.Topics
        : topicGroupForReport(props.currentPage).length > 0
          ? topicGroupForReport(props.currentPage)[0]
          : []

    switch (pageContent.screenshot.type) {
      case "axes":
        return (
          <ReportScreenshotAxesWidget
            currentSurvey={props.survey.active}
            isFullWidth={!props.currentPage.twoColumns}
            currentPage={props.currentPage}
          />
        )
      case "heatmap":
        return (
          <ReportScreenshotHeatmapWidget
            resultsType={pageContent.screenshot.resultsType}
            filterName={pageContent.screenshot.filterName}
            screenshot={pageContent.screenshot}
            nativeFiltersLabels={{
              gender: pageContent.screenshot.gender ? getScreenshotParamValue("gender") : null,
              birthDate: pageContent.screenshot.birthDate ? getScreenshotParamValue("birthDate") : null,
              companyWelcomeDate: pageContent.screenshot.companyWelcomeDate
                ? getScreenshotParamValue("companyWelcomeDate")
                : null
            }}
          />
        )
      case "global_results":
        return (
          <ReportScreenshotGlobalResultsWidget
            currentSurvey={props.survey.active}
            customTitle={pageContent.screenshot.customTitle}
            currentPage={props.currentPage}
          />
        )
      case "history":
        return <ReportScreenshotHistoryWidget currentTopics={currentTopics} />
      case "participation":
        return <ReportScreenshotParticipationWidget isFullWidth={!props.currentPage.twoColumns} />
      case "podium":
        return (
          <ReportScreenshotPodiumWidget
            currentSurvey={props.survey.active}
            isFullWidth={!props.currentPage.twoColumns}
            displayQuestions={pageContent.screenshot.displayQuestions}
          />
        )
      case "questions":
        return (
          <ReportScreenshotQuestionsWidget
            currentPage={props.currentPage}
            displayFullScale={pageContent.screenshot.displayFullScale}
            currentTopics={currentTopics}
          />
        )
      case "topics":
        return (
          <ReportScreenshotQuestionsWidget currentPage={props.currentPage} topicsOnly currentTopics={currentTopics} />
        )
      case "messages":
        if (!props._session.isTabDisplayed("/dashboard/messages")) return null
        return (
          <ReportScreenshotMessagesWidget
            isFullWidth={!props.currentPage.twoColumns}
            currentPage={props.currentPage}
            currentTopic={props.currentPage.Topics.length > 0 ? props.currentPage.Topics[0] : new Topic()}
          />
        )
      case "nps":
        return <ReportScreenshotNpsWidget isFullWidth={!props.currentPage.twoColumns} isReport />
      case "repartition":
        return <ReportScreenshotRepartitionWidget isFullWidth={!props.currentPage.twoColumns} isReport />
      case "rps_overview":
        return <DashboardRpsOverviewWidget dashboardFilters={props.filter.dashboard} isReport />
      case "rps_teams":
        return <ReportScreenshotRpsTeamsWidget isFullWidth={!props.currentPage.twoColumns} isReport />
      case "rps_questions":
        return (
          <ReportScreenshotRpsQuestionsWidget
            currentPage={props.currentPage}
            isFullWidth={!props.currentPage.twoColumns}
            dashboardFilters={props.filter.dashboard}
            isReport
          />
        )
      case "rps_users":
        return (
          <div
            style={{
              transform: "scale(0.4)",
              transformOrigin: "top left",
              pointerEvents: "none",
              marginTop: -24
            }}>
            <DashboardRpsUsersWidget dashboardFilters={props.filter.dashboard} isReport />
          </div>
        )
      case "rps_gollac_overview":
        return (
          <div
            style={{
              transform: "scale(0.4)",
              transformOrigin: "top left",
              pointerEvents: "none",
              marginTop: -15
            }}>
            <DashboardRpsGollacOverwiewWidget isReport />
          </div>
        )
      case "rps_gollac_teams":
        return (
          <div
            style={{
              transform: "scale(0.4)",
              transformOrigin: "top left",
              pointerEvents: "none",
              marginTop: -15
            }}>
            <DashboardRpsGollacTeamsWidget isReport />
          </div>
        )
      case "rps_gollac_topics":
        return (
          <div
            style={{
              transform: "scale(0.4)",
              transformOrigin: "top left",
              pointerEvents: "none",
              marginTop: -15
            }}>
            <DashboardRpsGollacQuestionsWidget
              dashboardFilters={props.filter.dashboard}
              isReport
              isPreview
              limit={6}
              offset={props.currentPage.rpsGollacOffset}
              zone={props.currentPage.rpsGollacZone as "exposed" | "safe" | undefined}
            />
          </div>
        )
      case "rps_gollac_steps":
        return (
          <div
            style={{
              transform: "scale(0.4)",
              transformOrigin: "top left",
              pointerEvents: "none",
              marginTop: -15
            }}>
            <DashboardRpsGollacWidget
              isReport
              isPreview
              limit={8}
              offset={props.currentPage.rpsGollacOffset}
              isFirstPage={props.currentPage.rpsGollacIsFirstPage}
              resultsType={pageContent.screenshot.resultsType}
            />
          </div>
        )
      default:
        return
    }
  }

  //List of options for each params
  function getScreenshotParamOptions(param: PageContentScreenshotParam) {
    switch (param) {
      case "filterName":
        return props.filter.list
      case "resultsType":
        return ACCOUNT_RESULTS_TYPES.map((x) => {
          return { id: x, name: t(x) }
        })
      case "gender":
        return [
          { id: "m", name: t("user_gender_m") },
          { id: "f", name: t("user_gender_f") }
        ]
      case "birthDate": {
        const ceils = props._session.accountOptions.ceilsBirthDate
        return [
          { id: "0", name: `Moins de ${ceils[0]} ans` },
          { id: "1", name: `Entre ${ceils[0]} et ${ceils[1] - 1} ans` },
          { id: "2", name: `${ceils[1]} ans ou plus` }
        ]
      }
      case "companyWelcomeDate": {
        const ceils = props._session.accountOptions.ceilsCompanyWelcomeDate
        const years1 = Math.round(ceils[0] / 12)
        const years2 = Math.round(ceils[1] / 12)
        return [
          { id: "0", name: `Moins de ${years1} ans` },
          { id: "1", name: `Entre ${years1} et ${years2} ans` },
          { id: "2", name: `${years2} ans ou plus` }
        ]
      }
      default:
        return [
          { id: true, name: t("utils_yes") },
          { id: false, name: t("utils_no") }
        ]
    }
  }

  //Get value for the parameters for screenshot
  function getScreenshotParamValue(param: PageContentScreenshotParam) {
    const options: any = getScreenshotParamOptions(param)
    const option: any = options.find((x) => x.id === pageContent.screenshot[param])
    return option ? option.name : ""
  }

  //Get all type for the screenshot
  //If page is right do not allow fullscreen widgets
  function getScreenshotTypes() {
    return PAGE_CONTENT_SCREENSHOT_CONFIGS.filter((x) => {
      if ((x.type === "rps_teams" || x.type === "rps_questions") && !props._session?.modules?.survey?.rps) {
        return false
      }
      return true
    }).map((x) => ({
      id: x.type,
      name: t("page_type_screenshot_" + x.type),
      params: x.params,
      hideButton: x.hideButton
    }))
  }

  //Quit edit mode on escape
  function keyDownEdited(e) {
    if (e.key === "Escape") {
      setIsEdited(false)
    }
  }

  //Convert text to raw
  function onEditorStateChange(e: EditorState) {
    setEditorState(e)
  }

  //Open edit mode
  //Do not apply if small preview (nav bar top)
  function openEditMode() {
    if (!props.isSmall) {
      setIsEdited(true)
      setIsClicked(true)
      setEditorState(pageFormatEditorState(props.pageContent.textBlocks, false))
    }
  }

  return (
    <div
      className="flex1 flex flex-auto"
      ref={ref}
      style={{
        padding: "2%",
        maxHeight: 300,
        overflow: pageContent.type === "text" && !props.isSmall ? "auto" : "hidden",
        marginTop: pageContent.type === "screenshot" && isEdited ? 32 : undefined
      }}>
      {isEdited ? (
        <div className="report-page-content" onKeyDown={keyDownEdited}>
          <FadeIn className="abs report-page-content-type report-page-editor-toolbar flex-wrap flex">
            <EditbarButton
              onClick={() => edit("type", "text")}
              isActive={pageContent.type === "text"}
              icon={faFont}
              text={t("page_type_text")}
            />

            <Space />

            <div className="flex flex-wrap" style={{ width: 244 }}>
              {getScreenshotTypes().map((screenshotType) => (
                <div key={screenshotType.id} className="flex" style={{ height: 0 }}>
                  {!screenshotType.hideButton && (
                    <EditbarButton
                      isSmall
                      isActive={pageContent.type === "screenshot" && pageContent.screenshot.type === screenshotType.id}
                      onClick={() => {
                        edit("type", "screenshot")
                        editScreenshot("type", screenshotType.id)
                      }}
                      screenshotType={screenshotType.id}
                      text={screenshotType.name}
                    />
                  )}

                  {pageContent.type === "screenshot" &&
                    pageContent.screenshot.type === screenshotType.id &&
                    screenshotType.params.length > 0 && (
                      <div
                        style={{
                          display: "flex",
                          width: "max-content",
                          gap: 16,
                          position: "absolute",
                          left: 92,
                          top: 8
                        }}>
                        {/* Custom filters (left) */}
                        <Space />
                        <div>
                          {screenshotType.params
                            .filter((p) => !["gender", "birthDate", "companyWelcomeDate"].includes(p))
                            .map((param) => (
                              <div key={param}>
                                <div style={{ fontSize: 10, padding: "0 4px" }}>
                                  {t("page_type_screenshot_param_" + snakeCase(param))}
                                </div>

                                <ListDropdown
                                  active={pageContent.screenshot[param]}
                                  onSelect={(e) => editScreenshot(param, e.id)}
                                  value={getScreenshotParamValue(param)}
                                  values={getScreenshotParamOptions(param)}
                                />
                              </div>
                            ))}
                        </div>

                        {/* Native filters (right) */}
                        {screenshotType.params.some((p) =>
                          ["gender", "birthDate", "companyWelcomeDate"].includes(p)
                        ) && (
                          <div
                            style={{
                              width: "max-content"
                            }}
                            className="flex-col">
                            {screenshotType.params
                              .filter((p) => ["gender", "birthDate", "companyWelcomeDate"].includes(p))
                              .map((param) => (
                                <div key={param}>
                                  <div style={{ fontSize: 10, padding: "0 4px" }}>
                                    {t("page_type_screenshot_param_" + snakeCase(param))}
                                  </div>

                                  <ListDropdown
                                    active={pageContent.screenshot[param]}
                                    onSelect={(e) => editScreenshot(param, e.id)}
                                    onClear={() => editScreenshot(param, "")}
                                    value={getScreenshotParamValue(param)}
                                    values={getScreenshotParamOptions(param)}
                                  />
                                </div>
                              ))}
                          </div>
                        )}
                        <Space />
                      </div>
                    )}
                </div>
              ))}

              <div style={{ height: 0 }} className="flex">
                <EditbarButton
                  onClick={() => edit("type", "image")}
                  isSmall
                  isActive={pageContent.type === "image"}
                  icon={faImage}
                  text={t("page_type_image")}
                />
              </div>
            </div>
          </FadeIn>

          {pageContent.type === "text" && (
            <Editor
              toolbar={{
                options: ["colorPicker", "inline", "fontSize"],
                inline: { options: ["bold"] },
                fontSize: { options: [6, 8, 10, 14, 18, 24, 32, 36, 42] },
                colorPicker: {
                  colors: ["#111C2B", "#8C8C8C", "#1b759a", "#20CA7E", "#ff9500", "#eb5a46"]
                }
              }}
              editorState={editorState}
              wrapperClassName="report-page-editor"
              editorClassName="report-page-editor-textarea"
              onEditorStateChange={(e) => onEditorStateChange(e)}
            />
          )}

          {pageContent.type === "image" && (
            <ImagePicker
              id={imageId}
              square
              imageId={pageContent.imageId ? pageContent.imageId : null}
              model="ReportPages"
              onCropChange={setIsCropping}
              onDelete={() => edit("imageId", null)}
              onSave={() => edit("imageId", imageId)}
            />
          )}

          {pageContent.type === "screenshot" && getScreenshot()}

          {pageContent.type === "screenshot" &&
            !pageContent.screenshot.displayAll &&
            ["topics", "questions", "messages"].indexOf(pageContent.screenshot.type ?? "") > -1 && (
              <div
                style={{
                  height: 342,
                  overflowY: "auto",
                  borderRadius: "0px 0px 6px 0px",
                  marginTop: 41,
                  position: "absolute",
                  top: 49,
                  right: 0,
                  backgroundColor: "#2d2d2d",
                  color: "white",
                  zIndex: 1
                }}>
                <ListItem isSmall>
                  <Link
                    isWithoutMargin
                    onClick={() =>
                      editScreenshot(
                        "hideItems",
                        pageContent.screenshot.hideItems.length === props.topic.list.length
                          ? []
                          : props.topic.list.map((x) => x.aid)
                      )
                    }>
                    {t(
                      pageContent.screenshot.hideItems.length === props.topic.list.length
                        ? "page_question_activate_all"
                        : "page_questions_unable_all"
                    )}
                  </Link>
                </ListItem>

                {props.axis.list.map((axis) => {
                  const axisTopics = props.topic.list.filter((topic) => topic.AxisId === axis.id)
                  const isAxisSelected =
                    axisTopics.length > 0 && axisTopics.every((topic) => !pageContent.screenshot.isTopicHidden(topic))

                  return (
                    <div key={axis.id}>
                      <ListItem isSmall>
                        <Checkbox
                          active={isAxisSelected}
                          disabled={!axisTopics.length}
                          className="font-bold"
                          text={axis.label}
                          onClick={() => {
                            const topicIds = axisTopics.map((topic) => topic.aid).filter(Boolean)
                            editScreenshot(
                              "hideItems",
                              isAxisSelected
                                ? Array.from(new Set([...pageContent.screenshot.hideItems, ...topicIds]))
                                : pageContent.screenshot.hideItems.filter((id) => !topicIds.includes(id))
                            )
                          }}
                        />
                        <Space />
                      </ListItem>

                      {axisTopics.map((topic) => (
                        <div key={topic.id}>
                          <ListItem isSmall>
                            <Checkbox
                              active={!pageContent.screenshot.isTopicHidden(topic)}
                              onClick={() => displayTopic(topic)}
                              text={topic.label}
                            />
                            <Space />
                          </ListItem>
                        </div>
                      ))}
                    </div>
                  )
                })}

                {props.topic.list
                  .filter((x) => !x.AxisId)
                  .map((topic) => (
                    <div key={topic.id}>
                      <ListItem isSmall>
                        <Checkbox
                          active={!pageContent.screenshot.isTopicHidden(topic)}
                          onClick={() => displayTopic(topic)}
                          text={topic.label}
                        />
                        <Space />
                      </ListItem>
                    </div>
                  ))}
              </div>
            )}
        </div>
      ) : (
        <div className="report-page-content _hover" onClick={openEditMode}>
          {pageContent.type === "text" && (
            <div style={{ pointerEvents: "none" }}>
              <Editor
                toolbarHidden
                placeholder={t("page_placeholder_description")}
                editorState={editorStateFormatted}
                readOnly
              />
            </div>
          )}

          {pageContent.type === "image" && pageContent.imageId && (
            <img
              src={env.REACT_APP_URL_SPACE + "/ReportPages/" + pageContent.imageId + ".png"}
              id={"report-image-" + pageContent.imageId}
              alt={"image" + pageContent.imageId}
              style={{ width: "75%" }}
            />
          )}

          {pageContent.type === "screenshot" &&
            (props._session.interfaceType === "SUPERVISOR"
              ? t("page_type_screenshot_" + pageContent.screenshot.type)
              : getScreenshot())}
        </div>
      )}
    </div>
  )
}

const mapStateToProps = (state) => ({
  _session: state._session,
  axis: state.axis,
  filter: state.filter,
  page: state.page,
  report: state.report,
  survey: state.survey,
  topic: state.topic
})

export default connect(mapStateToProps)(withTranslation()(ReportPageContentWidget))
